import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { User, Mail, Building, Lock, Save, Eye, EyeOff } from 'lucide-react';

export default function SettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [saving, setSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.updateProfile({ name, company });
      await refreshUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (newPw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      await api.changePassword({ current_password: currentPw, new_password: newPw });
      toast.success('Password changed! Please log in again.');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setTimeout(() => logout(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPw(false); }
  };

  return (
    <div className="max-w-2xl animate-fade-in-up opacity-0" data-testid="settings-page">
      <h1 className="text-3xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo' }}>Settings</h1>
      <p className="text-white/40 mb-8">Manage your account and preferences</p>

      {/* Profile Section */}
      <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 mb-6" data-testid="profile-section">
        <h2 className="text-lg font-bold mb-4">Profile</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 flex items-center gap-2"><User className="w-3 h-3" /> Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="forge-input w-full" data-testid="settings-name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 flex items-center gap-2"><Mail className="w-3 h-3" /> Email</label>
            <input value={user?.email || ''} readOnly className="forge-input w-full opacity-60 cursor-not-allowed" data-testid="settings-email" />
            <p className="text-[10px] text-white/20 mt-1">Email cannot be changed</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 flex items-center gap-2"><Building className="w-3 h-3" /> Company</label>
            <input value={company} onChange={e => setCompany(e.target.value)} className="forge-input w-full" data-testid="settings-company-input" />
          </div>
          <button onClick={handleSaveProfile} disabled={saving} className="btn-primary text-sm flex items-center gap-2" data-testid="save-profile-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 mb-6" data-testid="password-section">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</h2>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Current Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="forge-input w-full pr-10" data-testid="current-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">New Password</label>
            <input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="forge-input w-full" placeholder="Min 8 characters" data-testid="new-password-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Confirm New Password</label>
            <input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="forge-input w-full" data-testid="confirm-password-input" />
          </div>
          <button onClick={handleChangePassword} disabled={changingPw || !currentPw || !newPw} className="btn-secondary text-sm flex items-center gap-2" data-testid="change-password-btn">
            <Lock className="w-4 h-4" /> {changingPw ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>

      {/* Plan Info */}
      <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Plan</p>
            <p className="text-xs text-white/40 capitalize">{user?.plan || 'free'} — {user?.generations_used || 0}/{user?.generations_limit || 10} used</p>
          </div>
          <span className="px-3 py-1 bg-[#CDFE65]/10 text-[#CDFE65] text-xs font-bold uppercase rounded-full">{user?.plan || 'free'}</span>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-[#0F0F11] border border-red-500/20 rounded-xl p-6">
        <h2 className="text-lg font-bold text-red-400 mb-2">Danger Zone</h2>
        <p className="text-xs text-white/30 mb-4">Sign out of your account</p>
        <button onClick={logout} className="text-sm text-red-400 hover:text-red-300 transition-colors border border-red-500/20 px-4 py-2 rounded-full hover:bg-red-500/10" data-testid="logout-btn">Sign out</button>
      </div>
    </div>
  );
}
